// src/lib/authFile.js
// 非公開のアップロードファイル（申請添付・お知らせ添付・社員写真など）を、
// ログイン中のトークンを付けて配信 API（/api/files/...）から取得する共通部品。
// <img src> や <a href> ではトークンを送れないため、Blob で取得して object URL にする。
// 詳細: docs/spec_file_access.md 7 章
import { useEffect, useState } from "react";
import api from "./api";

// lib/api の baseURL は "/api" なので、"/api/files/..." の先頭の /api を外して渡す
const toApiPath = (url) => String(url || "").replace(/^\/api(?=\/)/, "");

export async function fetchAuthFile(url) {
  const res = await api.get(toApiPath(url), { responseType: "blob" });
  return res.data;
}

/** 画面に出すエラーメッセージ（403・404 とそれ以外） */
export function authFileErrorMessage(err) {
  const status = err?.response?.status;
  if (status === 403) return "このファイルを開く権限がありません。";
  if (status === 404) return "ファイルが見つかりません（削除された可能性があります）。";
  return "ファイルを開けませんでした。時間をおいて再度お試しください。";
}

/**
 * ファイルを取得して新しいタブで開く（PDF・画像）。
 * ポップアップを止められないよう、クリック直後に空のタブを開いてから中身を差し込む。
 * タブを開けない環境ではダウンロードにする。失敗時は例外を投げる（呼び出し側でメッセージ表示）。
 */
export async function openAuthFile(url, filename) {
  const win = window.open("", "_blank");
  try {
    const blob = await fetchAuthFile(url);
    const objectUrl = URL.createObjectURL(blob);
    if (win) {
      win.location.href = objectUrl;
    } else {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}

/** Blob を取得して object URL を返す（PDF ビューア等に渡す用）。不要になったら revokeObjectURL すること */
export async function getAuthFileObjectUrl(url) {
  const blob = await fetchAuthFile(url);
  return URL.createObjectURL(blob);
}

/**
 * <img> 用。url の画像を取得し、object URL を返す。取得中・失敗時は null。
 * url が変わったら取り直し、アンマウント時に object URL を解放する。
 */
export function useAuthImage(url) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!url) {
      setSrc(null);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    fetchAuthFile(url)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return src;
}

/** useAuthImage をコンポーネントとして使う（一覧の各行など、フックを直接呼べない場所用） */
export function AuthImage({ url, alt = "", className, fallback = null, ...rest }) {
  const src = useAuthImage(url);
  if (!src) return fallback;
  return <img src={src} alt={alt} className={className} {...rest} />;
}
